import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const GREETING = {
  role: "assistant",
  text: "Hey — I'm VOLT, your AI tech guru. Ask me for picks, comparisons or specs. What are you building?",
};
const SUGGESTIONS = ["Best phone under $1300?", "Compare the two laptops", "What's hot in audio?"];

export default function AssistantWidget() {
  const { assistantOpen, setAssistantOpen } = useCart();
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("voltaic_sid");
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem("voltaic_sid", s);
    }
    return s;
  });
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      const el = listRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  useEffect(() => {
    if (!assistantOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/assistant/history/${sessionId}`);
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setMessages([GREETING, ...data.map((m) => ({ role: m.role, text: m.text }))]);
          scrollDown();
        }
      } catch {
        /* first session */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantOpen]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setBusy(true);
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    scrollDown();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", text: acc };
          return c;
        });
        scrollDown();
      }
      if (!acc) {
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", text: "My neural core hiccuped — try again in a moment." };
          return c;
        });
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Connection to my neural core dropped. Try again in a moment." };
        return c;
      });
    }
    setBusy(false);
  };

  return (
    <>
      <motion.button
        data-testid="assistant-open-button"
        onClick={() => setAssistantOpen(true)}
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.08 }}
        className="fixed bottom-6 right-6 z-40 grid h-14 w-14 place-items-center rounded-full bg-voltaic-cyan text-voltaic-bg shadow-[0_0_36px_rgba(0,240,255,0.45)]"
        aria-label="Open VOLT AI assistant"
      >
        <Sparkles size={22} />
        {!assistantOpen && (
          <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-voltaic-bg bg-voltaic-pink" />
        )}
      </motion.button>

      <AnimatePresence>
        {assistantOpen && (
          <motion.div
            key="assistant-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="assistant-panel"
            className="fixed bottom-6 right-6 z-50 flex h-[540px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-voltaic-line bg-voltaic-bg/95 shadow-[0_24px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          >
            <div className="flex items-center justify-between border-b border-voltaic-line px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-voltaic-cyan/10 text-voltaic-cyan">
                  <Sparkles size={16} />
                </span>
                <div>
                  <p className="font-display text-sm font-bold uppercase tracking-widest">VOLT AI</p>
                  <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-voltaic-green">
                    <span className="h-1.5 w-1.5 animate-pulseDot rounded-full bg-voltaic-green" /> Online
                  </p>
                </div>
              </div>
              <button
                data-testid="assistant-close-button"
                onClick={() => setAssistantOpen(false)}
                aria-label="Close assistant"
                className="grid h-9 w-9 place-items-center rounded-full border border-voltaic-line text-voltaic-muted transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
              >
                <X size={15} />
              </button>
            </div>

            <div ref={listRef} data-testid="assistant-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, idx) => (
                <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`assistant-message-${idx}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-sm bg-voltaic-cyan text-voltaic-bg"
                        : "rounded-bl-sm border border-voltaic-line bg-voltaic-surface text-voltaic-ink"
                    }`}
                  >
                    {m.text || (busy && idx === messages.length - 1 ? "..." : "")}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`assistant-suggestion-${s.length}`}
                      onClick={() => send(s)}
                      className="rounded-full border border-voltaic-line px-3.5 py-1.5 text-xs text-voltaic-muted transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-voltaic-line p-3">
              <div className="flex items-center gap-2">
                <input
                  data-testid="assistant-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about specs, prices, picks..."
                  className="field flex-1 !rounded-full !py-2.5 text-sm"
                />
                <button
                  data-testid="assistant-send-button"
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  aria-label="Send message"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-voltaic-cyan text-voltaic-bg transition-shadow hover:glow-cyan disabled:opacity-40"
                >
                  <Send size={15} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
